import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { checklistService } from '../../../services/checklistService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const DailyTasksTab = ({ siteId, checklists, onRefresh, isReadOnly = false }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [completions, setCompletions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [checklistItems, setChecklistItems] = useState([{ category: 'Safety', itemDescription: '', isMandatory: true }]);
  const [itemResponses, setItemResponses] = useState({});
  const [completionNotes, setCompletionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (siteId && selectedDate) {
      loadCompletions();
    }
  }, [siteId, selectedDate]);

  const loadCompletions = async () => {
    try {
      setLoading(true);
      const result = await checklistService?.getChecklistCompletions(siteId, selectedDate);
      if (result?.data) {
        setCompletions(result?.data);
      }
    } catch (err) {
      console.error('Error loading completions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateName?.trim()) return;
    try {
      setSaving(true);
      const templateResult = await checklistService?.createChecklistTemplate({
        siteId,
        companyId: userProfile?.company_id,
        checklistName: templateName,
        description: templateDescription
      });

      if (templateResult?.data) {
        for (let i = 0; i < checklistItems?.length; i++) {
          const item = checklistItems?.[i];
          if (item?.itemDescription?.trim()) {
            await checklistService?.createChecklistItem({
              checklistId: templateResult?.data?.id,
              category: item?.category,
              itemDescription: item?.itemDescription,
              isMandatory: item?.isMandatory,
              displayOrder: i
            });
          }
        }
        setShowCreateModal(false);
        setTemplateName('');
        setTemplateDescription('');
        setChecklistItems([{ category: 'Safety', itemDescription: '', isMandatory: true }]);
        onRefresh();
      }
    } catch (err) {
      console.error('Error creating template:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteChecklist = async () => {
    if (!selectedChecklist) return;
    try {
      setSaving(true);
      const completionResult = await checklistService?.createChecklistCompletion({
        checklistId: selectedChecklist?.id,
        siteId,
        completionDate: selectedDate,
        notes: completionNotes
      });

      if (completionResult?.data) {
        for (const itemId of Object.keys(itemResponses)) {
          await checklistService?.createChecklistItemResponse({
            completionId: completionResult?.data?.id,
            checklistItemId: itemId,
            status: itemResponses?.[itemId]?.status || 'pending',
            comment: itemResponses?.[itemId]?.comment || null
          });
        }
        setShowCompleteModal(false);
        setSelectedChecklist(null);
        setItemResponses({});
        setCompletionNotes('');
        loadCompletions();
      }
    } catch (err) {
      console.error('Error completing checklist:', err);
    } finally {
      setSaving(false);
    }
  };

  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, { category: 'Safety', itemDescription: '', isMandatory: true }]);
  };

  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklistItems];
    updated[index][field] = value;
    setChecklistItems(updated);
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems?.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h3 className="text-lg font-semibold text-foreground">{t('dailyTasks')}</h3>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e?.target?.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          />
          {userProfile?.role === 'admin' && !isReadOnly && (
            <Button
              size="sm"
              iconName="Plus"
              onClick={() => setShowCreateModal(true)}
            >
              {t('createTemplate')}
            </Button>
          )}
          {userProfile?.role === 'worker' && checklists?.length > 0 && !isReadOnly && (
            <Button
              size="sm"
              iconName="CheckSquare"
              onClick={() => setShowCompleteModal(true)}
            >
              {t('completeTasks')}
            </Button>
          )}
        </div>
      </div>
      {checklists?.length > 0 ? (
        <div className="space-y-4">
          {checklists?.map((checklist) => {
            const completion = completions?.find(c => c?.checklist_id === checklist?.id);
            const isCompleted = !!completion;

            return (
              <div key={checklist?.id} className="bg-muted/50 rounded-lg border border-border p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0 ${
                      isCompleted ? 'bg-success/10' : 'bg-background'
                    }`}>
                      <Icon
                        name={isCompleted ? 'CheckCircle2' : 'CheckSquare'}
                        size={20}
                        color={isCompleted ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
                      />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-1">{checklist?.checklist_name}</h4>
                      {checklist?.description && (
                        <p className="text-sm text-muted-foreground">{checklist?.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{checklist?.checklist_items?.length || 0} {t('items')}</p>
                    </div>
                  </div>
                  {isCompleted && (
                    <span className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                      {t('completed')}
                    </span>
                  )}
                </div>
                {completion && (
                  <div className="text-xs text-muted-foreground">
                    {t('completedBy')} {completion?.user_profiles?.full_name} {t('at')} {new Date(completion?.created_at)?.toLocaleTimeString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-muted/50 rounded-lg p-12 border border-border text-center">
          <Icon name="CheckSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-foreground mb-2">{t('noChecklistsConfigured')}</h4>
          <p className="text-sm text-muted-foreground mb-4">{t('noChecklistsDesc')}</p>
          {userProfile?.role === 'admin' && !isReadOnly && (
            <Button
              size="sm"
              iconName="Plus"
              onClick={() => setShowCreateModal(true)}
            >
              {t('createTemplate')}
            </Button>
          )}
        </div>
      )}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-elevation-3 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">{t('createChecklistTemplate')}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('templateName')}</label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e?.target?.value)}
                  placeholder="e.g., Daily Safety Checklist"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">{t('descriptionOptional')}</label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e?.target?.value)}
                  placeholder="Brief description of this checklist"
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                  rows={3}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">{t('checklistItems')}</label>
                  <Button size="sm" iconName="Plus" onClick={addChecklistItem}>
                    {t('addItem')}
                  </Button>
                </div>
                <div className="space-y-3">
                  {checklistItems?.map((item, index) => (
                    <div key={index} className="flex items-start gap-2 bg-muted/50 p-3 rounded-lg">
                      <div className="flex-1 space-y-2">
                        <select
                          value={item?.category}
                          onChange={(e) => updateChecklistItem(index, 'category', e?.target?.value)}
                          className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                        >
                          <option value="Safety">Safety</option>
                          <option value="Quality">Quality</option>
                          <option value="Environment">Environment</option>
                          <option value="General">General</option>
                        </select>
                        <Input
                          value={item?.itemDescription}
                          onChange={(e) => updateChecklistItem(index, 'itemDescription', e?.target?.value)}
                          placeholder="Item description"
                        />
                        <label className="flex items-center gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={item?.isMandatory}
                            onChange={(e) => updateChecklistItem(index, 'isMandatory', e?.target?.checked)}
                            className="rounded border-border"
                          />
                          Required
                        </label>
                      </div>
                      {checklistItems?.length > 1 && (
                        <button
                          onClick={() => removeChecklistItem(index)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Icon name="X" size={20} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTemplate} disabled={saving || !templateName?.trim()}>
                {saving ? 'Creating...' : 'Create Template'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-elevation-3 border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h3 className="text-xl font-semibold text-foreground">Complete Daily Checklist</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Select Checklist</label>
                <select
                  value={selectedChecklist?.id || ''}
                  onChange={(e) => {
                    const checklist = checklists?.find(c => c?.id === e?.target?.value);
                    setSelectedChecklist(checklist);
                    const responses = {};
                    checklist?.checklist_items?.forEach(item => {
                      responses[item?.id] = { status: 'pending', comment: '' };
                    });
                    setItemResponses(responses);
                  }}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="">Choose a checklist...</option>
                  {checklists?.map(checklist => (
                    <option key={checklist?.id} value={checklist?.id}>
                      {checklist?.checklist_name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedChecklist && (
                <>
                  <div className="space-y-3">
                    {selectedChecklist?.checklist_items?.map((item) => (
                      <div key={item?.id} className="bg-muted/50 p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-medium text-foreground">{item?.item_description}</p>
                            <p className="text-xs text-muted-foreground">{item?.category}</p>
                          </div>
                          {item?.is_mandatory && (
                            <span className="text-xs text-destructive">Required</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          {['pass', 'fail', 'na']?.map(status => (
                            <button
                              key={status}
                              onClick={() => setItemResponses({
                                ...itemResponses,
                                [item?.id]: { ...itemResponses?.[item?.id], status }
                              })}
                              className={`px-3 py-1 rounded-md text-xs font-medium transition-smooth ${
                                itemResponses?.[item?.id]?.status === status
                                  ? status === 'pass' ? 'bg-success text-white'
                                  : status === 'fail'? 'bg-destructive text-white' :'bg-muted-foreground text-white' :'bg-background text-foreground border border-border'
                              }`}
                            >
                              {status === 'pass' ? 'Pass' : status === 'fail' ? 'Fail' : 'N/A'}
                            </button>
                          ))}
                        </div>
                        <Input
                          value={itemResponses?.[item?.id]?.comment || ''}
                          onChange={(e) => setItemResponses({
                            ...itemResponses,
                            [item?.id]: { ...itemResponses?.[item?.id], comment: e?.target?.value }
                          })}
                          placeholder="Optional comment"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Notes (Optional)</label>
                    <textarea
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e?.target?.value)}
                      placeholder="Additional notes or observations"
                      className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                      rows={3}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-border flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowCompleteModal(false);
                setSelectedChecklist(null);
                setItemResponses({});
                setCompletionNotes('');
              }}>
                Cancel
              </Button>
              <Button onClick={handleCompleteChecklist} disabled={saving || !selectedChecklist}>
                {saving ? 'Saving...' : 'Complete Checklist'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyTasksTab;